// Build/Export Type Definitions

export type BuildPlatform = 'web' | 'desktop' | 'mobile';

export type BuildConfiguration = 'development' | 'production';

export type WebFeature = 'pwa' | 'offline' | 'fullscreen' | 'autoOrientation';

export interface BuildTarget {
  platform: BuildPlatform;
  name: string;
  icon: string;
  description: string;
  enabled: boolean;
}

export interface BuildSettings {
  // General
  startingScene: string;
  outputDirectory: string;
  configuration: BuildConfiguration;
  targetResolution: { width: number; height: number };

  // Optimization
  minifyJs: boolean;
  compressImages: boolean;
  bundleAssets: boolean;
  removeUnused: boolean;
  generateSourceMaps: boolean;

  // Web-specific
  webFeatures: WebFeature[];
  serviceWorkerEnabled: boolean;
  manifestEnabled: boolean;

  // Desktop-specific (Electron)
  desktopIcon: string;
  desktopTitle: string;
  desktopFrame: boolean;
  desktopResizable: boolean;

  // Mobile-specific (Capacitor)
  mobileOrientation: 'portrait' | 'landscape' | 'any';
  mobileStatusBar: 'default' | 'hidden' | 'overlay';
  mobileSplashScreen: string;
}

export interface BuildOutput {
  path: string;
  name: string;
  type: string;
  size: number;
  compressed: boolean;
}

export interface BuildResult {
  success: boolean;
  platform: BuildPlatform;
  outputPath: string;
  files: BuildOutput[];
  totalSize: number;
  buildTime: number;
  errors: string[];
  warnings: string[];
}

export interface BuildProgress {
  stage: string;
  progress: number;
  message: string;
  substage?: string;
}

export interface BuildState {
  // Settings
  platform: BuildPlatform;
  settings: BuildSettings;

  // Build process
  isBuilding: boolean;
  progress: BuildProgress;

  // Results
  lastBuild: BuildResult | null;
  buildHistory: BuildResult[];

  // UI
  isOpen: boolean;
  showAdvanced: boolean;
  activeTab: 'settings' | 'optimization' | 'platform';
}

export interface BuildActions {
  // Dialog
  openDialog: () => void;
  closeDialog: () => void;

  // Settings
  setPlatform: (platform: BuildPlatform) => void;
  updateSettings: (settings: Partial<BuildSettings>) => void;
  resetSettings: () => void;

  // Build
  startBuild: () => Promise<BuildResult>;
  cancelBuild: () => void;

  // UI
  setActiveTab: (tab: BuildState['activeTab']) => void;
  toggleAdvanced: () => void;

  // History
  clearHistory: () => void;
}

// Default values
export const DEFAULT_BUILD_SETTINGS: BuildSettings = {
  startingScene: '',
  outputDirectory: './dist',
  configuration: 'production',
  targetResolution: { width: 720, height: 1560 },
  minifyJs: true,
  compressImages: true,
  bundleAssets: true,
  removeUnused: true,
  generateSourceMaps: false,
  webFeatures: ['pwa', 'fullscreen'],
  serviceWorkerEnabled: true,
  manifestEnabled: true,
  desktopIcon: '',
  desktopTitle: 'My Game',
  desktopFrame: true,
  desktopResizable: true,
  mobileOrientation: 'landscape',
  mobileStatusBar: 'hidden',
  mobileSplashScreen: '',
};

export const BUILD_TARGETS: BuildTarget[] = [
  {
    platform: 'web',
    name: 'Web / HTML5',
    icon: 'Globe',
    description: 'Deploy to any web server or host',
    enabled: true,
  },
  {
    platform: 'desktop',
    name: 'Desktop (Electron)',
    icon: 'Monitor',
    description: 'Windows, macOS, Linux executables',
    enabled: true,
  },
  {
    platform: 'mobile',
    name: 'Mobile (Capacitor)',
    icon: 'Smartphone',
    description: 'iOS and Android apps',
    enabled: false,
  },
];

export const BUILD_STAGES = [
  { id: 'prepare', label: 'Preparing build' },
  { id: 'compile', label: 'Compiling scripts' },
  { id: 'bundle', label: 'Bundling assets' },
  { id: 'optimize', label: 'Optimizing' },
  { id: 'package', label: 'Packaging' },
  { id: 'finalize', label: 'Finalizing' },
];

// Utility functions
export function formatBuildSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function formatBuildTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds % 60}s`;
}
